/**
 * Rymar Grass — Dealer/Locations Locator JavaScript
 * Adapted from the Vintage Iron architecture to support Office and Service City blocks.
 */

class RymarDealerLocator {
    constructor(sectionId) {
        this.sectionId = sectionId;
        this.data = window.rymarDealerLocatorData[sectionId];
        this.dealers = this.data.dealers;
        this.settings = this.data.settings;
        this.currentPage = 1;
        this.filteredDealers = [...this.dealers];
        this.map = null;
        this.markers = [];
        this.markerGroup = null;
        this.userLocation = null;
        this.activeProvinceFilter = '';
        this.currentView = 'both';
        this.usaMask = null;
        this.activeHighlightLayer = null;

        this.provinceAliases = {
            'ab': ['alberta', 'ab'],
            'bc': ['british columbia', 'bc', 'b.c.'],
            'mb': ['manitoba', 'mb'],
            'nb': ['new brunswick', 'nb'],
            'nl': ['newfoundland', 'newfoundland and labrador', 'nl', 'nfld'],
            'ns': ['nova scotia', 'ns'],
            'nt': ['northwest territories', 'nt', 'nwt'],
            'nu': ['nunavut', 'nu'],
            'on': ['ontario', 'on', 'ont'],
            'pe': ['prince edward island', 'pe', 'pei'],
            'qc': ['quebec', 'qc', 'que'],
            'sk': ['saskatchewan', 'sk', 'sask'],
            'yt': ['yukon', 'yt'],
            // Add US states loosely if Rymar expands
            'wa': ['washington', 'wa'],
            'ca': ['california', 'ca']
        };

        this.init();
    }

    init() {
        this.cacheElements();
        this.bindEvents();
        this.detectUserLocation();
        this.initPagination();
        this.initMap();
        this.initUsaMask();
    }

    initUsaMask() {
        if (typeof turf !== 'undefined') {
            this.usaMask = turf.polygon([[
                [-140.00, 47.00], 
                [-124.75, 48.38], 
                [-124.00, 48.30], 
                [-123.20, 48.15], 
                [-123.00, 48.50], 
                [-123.10, 48.80], 
                [-123.01, 49.00], 
                [-95.15, 49.00],  
                [-95.15, 48.80], 
                [-94.00, 48.50], 
                [-89.50, 48.00],  
                [-84.50, 46.50],  
                [-82.50, 45.00],  
                [-82.35, 43.00],  
                [-82.60, 42.50],  
                [-83.15, 42.30],  
                [-83.15, 42.00],  
                [-79.00, 42.80],  
                [-79.05, 43.20],  
                [-78.50, 43.40],  
                [-76.50, 44.00],  
                [-74.60, 45.00],  
                [-71.50, 45.00],  
                [-71.00, 45.30],  
                [-70.50, 45.80],  
                [-69.20, 47.45],  
                [-67.70, 45.70],  
                [-67.30, 45.20],  
                [-66.95, 44.80],  
                [-66.95, 42.00],  
                [-60.00, 40.00], 
                [-60.00, 20.00],
                [-140.00, 20.00],
                [-140.00, 47.00]
            ]]);
        }
    }

    cacheElements() {
        this.searchInput = document.querySelector('[data-rymar-dealer-search]');
        this.searchClearBtn = document.querySelector('[data-rymar-search-clear]');
        this.provinceSelect = document.querySelector('[data-rymar-province-filter]');
        this.dealersGrid = document.querySelector('[data-rymar-dealers-grid]');
        this.dealerCards = this.dealersGrid
            ? Array.from(this.dealersGrid.querySelectorAll('.rymar-dealer-card'))
            : [];
        this.dealersWrapper = document.querySelector('.rymar-dealer-locator__dealers');
        this.contentWrapper = document.querySelector('.rymar-dealer-locator__content');
        this.noResultsEl = document.querySelector('[data-rymar-no-results]');
        this.paginationPrev = document.querySelector('[data-rymar-pagination-prev]');
        this.paginationNext = document.querySelector('[data-rymar-pagination-next]');
        this.paginationPages = Array.from(document.querySelectorAll('[data-rymar-pagination-page]'));
        this.mapContainer = document.querySelector('[data-rymar-dealer-map]');
        this.mapWrapper = document.querySelector('.rymar-dealer-locator__map-wrapper');
        this.mapLoading = document.querySelector('.rymar-dealer-locator__map-loading');
        this.toggleBtns = document.querySelectorAll('[data-rymar-toggle-view]');
    }

    bindEvents() {
        var self = this;

        if (this.searchInput) {
            this.searchInput.addEventListener('input', this.handleSearch.bind(this));
        }
        if (this.searchClearBtn) {
            this.searchClearBtn.addEventListener('click', this.clearSearch.bind(this));
        }
        if (this.provinceSelect) {
            this.provinceSelect.addEventListener('change', function () {
                self.activeProvinceFilter = this.value.toLowerCase();
                self.applyFilters();
            });
        }
        this.toggleBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                self.toggleView(this.dataset.rymarToggleView);
            });
        });
        if (this.paginationPrev) {
            this.paginationPrev.addEventListener('click', function () {
                self.goToPage(self.currentPage - 1);
            });
        }
        if (this.paginationNext) {
            this.paginationNext.addEventListener('click', function () {
                self.goToPage(self.currentPage + 1);
            });
        }
        this.paginationPages.forEach(function (btn) {
            btn.addEventListener('click', function (e) {
                self.goToPage(parseInt(e.target.dataset.rymarPaginationPage));
            });
        });
        this.dealerCards.forEach(function (card) {
            card.addEventListener('click', function (e) {
                if (e.target.closest('a')) return;
                self.highlightMarkerFromCard(card);
            });
            card.addEventListener('mouseenter', function () {
                if (card.classList.contains('rymar-dealer-card--office')) return;
                var lat = parseFloat(card.dataset.dealerLat);
                var lng = parseFloat(card.dataset.dealerLng);
                if (!isNaN(lat) && !isNaN(lng)) {
                    self.showServiceArea(lat, lng);
                }
            });
            card.addEventListener('mouseleave', function () {
                self.hideServiceArea();
            });
        });
    }

    toggleView(view) {
        this.currentView = view;
        this.toggleBtns.forEach(function (btn) {
            if (btn.dataset.rymarToggleView === view) {
                btn.classList.add('rymar-dealer-locator__toggle-btn--active');
            } else {
                btn.classList.remove('rymar-dealer-locator__toggle-btn--active');
            }
        });

        if (this.mapWrapper) this.mapWrapper.classList.remove('rymar-dealer-locator__map-wrapper--hidden');
        if (this.dealersWrapper) this.dealersWrapper.classList.remove('rymar-dealer-locator__dealers--hidden');
        if (this.contentWrapper) {
            this.contentWrapper.classList.remove('rymar-dealer-locator__content--map-only');
        }

        if (view === 'map') {
            if (this.dealersWrapper) this.dealersWrapper.classList.add('rymar-dealer-locator__dealers--hidden');
            if (this.contentWrapper) this.contentWrapper.classList.add('rymar-dealer-locator__content--map-only');
        } else if (view === 'list') {
            if (this.mapWrapper) this.mapWrapper.classList.add('rymar-dealer-locator__map-wrapper--hidden');
        }

        if (view !== 'list' && this.map) {
            var map = this.map;
            setTimeout(function () { map.invalidateSize(); }, 150);
        }
    }

    showServiceArea(lat, lng) {
        if (!this.map || typeof turf === 'undefined' || !this.usaMask) return;
        this.hideServiceArea();
        
        var circle = turf.circle([lng, lat], 250, {steps: 64, units: 'kilometers'});
        var finalGeoJSON = circle;
        
        try {
            var clipped = turf.difference(circle, this.usaMask);
            if (clipped) {
                finalGeoJSON = clipped;
            }
        } catch (e) {
            console.warn('Turf difference failed, drawing full circle.', e);
        }
        
        var style = {
            color: '#1F7A3E',
            weight: 2,
            fillColor: '#1F7A3E',
            fillOpacity: 0.15,
            dashArray: '5, 5'
        };

        this.activeHighlightLayer = L.geoJSON(finalGeoJSON, { style: style }).addTo(this.map);
    }

    hideServiceArea() {
        if (this.activeHighlightLayer && this.map) {
            this.map.removeLayer(this.activeHighlightLayer);
            this.activeHighlightLayer = null;
        }
    }

    detectUserLocation() {
        var self = this;
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    self.userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
                    self.onLocationDetected();
                },
                function () { self.fallbackIPLocation(); },
                { timeout: 8000, maximumAge: 300000 }
            );
        } else {
            self.fallbackIPLocation();
        }
    }

    fallbackIPLocation() {
        var self = this;
        fetch('https://ipapi.co/json/')
            .then(function (res) { return res.json(); })
            .then(function (data) {
                if (data.latitude && data.longitude) {
                    self.userLocation = { lat: data.latitude, lng: data.longitude };
                    self.onLocationDetected();
                }
            })
            .catch(function () { });
    }

    onLocationDetected() {
        console.log("Location detected, sorting by proximity:", this.userLocation);
        this.updateDistanceBadges();
        this.sortCardsByDistance();
        this.currentPage = 1;
        this.updateDisplay();
        this.updateMapMarkers();
    }

    updateDistanceBadges() {
        if (!this.userLocation) return;
        var self = this;
        this.dealerCards.forEach(function (card) {
            var lat = parseFloat(card.dataset.dealerLat);
            var lng = parseFloat(card.dataset.dealerLng);
            var badge = card.querySelector('[data-rymar-dealer-distance]');
            if (badge && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                var dist = self.haversineDistance(self.userLocation.lat, self.userLocation.lng, lat, lng);
                card._dealerDistance = dist;
                badge.textContent = dist < 100 ? dist.toFixed(0) + ' km' : Math.round(dist) + ' km';
                badge.style.display = '';
            } else {
                card._dealerDistance = 999999;
            }
        });
    }

    sortCardsByDistance() {
        if (!this.dealersGrid || !this.userLocation) return;
        var sortedCards = this.dealerCards.slice().sort(function (a, b) {
            return (a._dealerDistance || 999999) - (b._dealerDistance || 999999);
        });
        var fragment = document.createDocumentFragment();
        sortedCards.forEach(function (card, i) {
            card.dataset.dealerIndex = i;
            fragment.appendChild(card);
        });
        this.dealersGrid.appendChild(fragment);
        this.dealerCards = sortedCards;

        var self = this;
        this.dealers.sort(function (a, b) {
            var dA = (a.location.lat !== 0 && a.location.lng !== 0)
                ? self.haversineDistance(self.userLocation.lat, self.userLocation.lng, a.location.lat, a.location.lng) : 999999;
            var dB = (b.location.lat !== 0 && b.location.lng !== 0)
                ? self.haversineDistance(self.userLocation.lat, self.userLocation.lng, b.location.lat, b.location.lng) : 999999;
            return dA - dB;
        });
        this.filteredDealers = [...this.dealers];
    }

    haversineDistance(lat1, lon1, lat2, lon2) {
        var R = 6371;
        var dLat = (lat2 - lat1) * Math.PI / 180;
        var dLon = (lon2 - lon1) * Math.PI / 180;
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    handleSearch(e) {
        var query = e.target.value.toLowerCase().trim();
        if (this.searchClearBtn) {
            this.searchClearBtn.style.display = query ? 'flex' : 'none';
        }
        this.applyFilters();
    }

    applyFilters() {
        var query = this.searchInput ? this.searchInput.value.toLowerCase().trim() : '';
        var provinceDrop = this.activeProvinceFilter;
        var self = this;
        var matchingProvinceCodes = query ? this.resolveProvinceSearch(query) : [];

        this.filteredDealers = this.dealers.filter(function (dealer) {
            if (provinceDrop) {
                var dp = (dealer.address.province || '').toLowerCase().trim();
                if (dp !== provinceDrop) return false;
            }
            if (query) {
                var searchable = [
                    dealer.name, dealer.address.street, dealer.address.city,
                    dealer.address.province, dealer.address.country, dealer.servicing
                ].filter(Boolean).join(' ').toLowerCase();
                if (searchable.includes(query)) return true;
                if (matchingProvinceCodes.length > 0) {
                    var dp2 = (dealer.address.province || '').toLowerCase().trim();
                    return matchingProvinceCodes.includes(dp2);
                }
                return false;
            }
            return true;
        });

        this.currentPage = 1;
        this.updateDisplay();
        this.updateMapMarkers();
    }

    resolveProvinceSearch(query) {
        var matchedCodes = [];
        var self = this;
        Object.keys(this.provinceAliases).forEach(function (code) {
            var aliases = self.provinceAliases[code];
            for (var i = 0; i < aliases.length; i++) {
                if (aliases[i].includes(query) || query.includes(aliases[i])) {
                    if (matchedCodes.indexOf(code) === -1) matchedCodes.push(code);
                    break;
                }
            }
        });
        return matchedCodes;
    }

    clearSearch() {
        if (this.searchInput) this.searchInput.value = '';
        if (this.searchClearBtn) this.searchClearBtn.style.display = 'none';
        this.applyFilters();
        if (this.searchInput) this.searchInput.focus();
    }

    initPagination() { this.updateDisplay(); }

    goToPage(page) {
        var totalPages = Math.ceil(this.filteredDealers.length / this.settings.itemsPerPage);
        if (page < 1 || page > totalPages) return;
        this.currentPage = page;
        this.updateDisplay();
        if (this.dealersGrid) {
            var offsetTop = this.dealersGrid.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
    }

    updateDisplay() {
        var itemsPerPage = this.settings.itemsPerPage;
        var startIndex = (this.currentPage - 1) * itemsPerPage;
        var endIndex = startIndex + itemsPerPage;
        var totalPages = Math.ceil(this.filteredDealers.length / itemsPerPage);
        var self = this;

        this.dealerCards.forEach(function (card) {
            var dealerIndex = parseInt(card.dataset.dealerIndex);
            var dealer = self.dealers[dealerIndex];
            var filteredIndex = self.filteredDealers.indexOf(dealer);
            var isInPage = filteredIndex >= startIndex && filteredIndex < endIndex;
            if (filteredIndex > -1 && isInPage) {
                card.classList.remove('hidden');
            } else {
                card.classList.add('hidden');
            }
        });

        if (this.noResultsEl) {
            this.noResultsEl.style.display = this.filteredDealers.length === 0 ? 'block' : 'none';
        }
        this.updatePaginationControls(totalPages);
    }

    updatePaginationControls(totalPages) {
        var self = this;
        if (this.paginationPrev) this.paginationPrev.disabled = this.currentPage === 1;
        if (this.paginationNext) this.paginationNext.disabled = this.currentPage === totalPages || totalPages === 0;
        this.paginationPages.forEach(function (btn) {
            var page = parseInt(btn.dataset.rymarPaginationPage);
            if (page === self.currentPage) {
                btn.classList.add('rymar-pagination-controls__page--active');
                btn.setAttribute('aria-current', 'page');
            } else {
                btn.classList.remove('rymar-pagination-controls__page--active');
                btn.removeAttribute('aria-current');
            }
            btn.style.display = page > totalPages ? 'none' : '';
        });
    }

    highlightCard(dealer) {
        var self = this;
        this.dealerCards.forEach(function (c) { c.classList.remove('rymar-dealer-card--highlight'); });
        var targetCard = null;
        for (var i = 0; i < this.dealerCards.length; i++) {
            var card = this.dealerCards[i];
            var lat = parseFloat(card.dataset.dealerLat);
            var lng = parseFloat(card.dataset.dealerLng);
            if (Math.abs(lat - dealer.location.lat) < 0.0001 && Math.abs(lng - dealer.location.lng) < 0.0001) {
                targetCard = card; break;
            }
        }
        if (!targetCard) return;
        if (this.currentView === 'map') this.toggleView('both');

        var dealerIndex = parseInt(targetCard.dataset.dealerIndex);
        var dealerObj = this.dealers[dealerIndex];
        var filteredIdx = this.filteredDealers.indexOf(dealerObj);
        if (filteredIdx > -1) {
            var neededPage = Math.floor(filteredIdx / this.settings.itemsPerPage) + 1;
            if (neededPage !== this.currentPage) {
                this.currentPage = neededPage;
                this.updateDisplay();
            }
        }

        targetCard.classList.add('rymar-dealer-card--highlight');
        setTimeout(function () { targetCard.classList.remove('rymar-dealer-card--highlight'); }, 3000);
    }

    highlightMarkerFromCard(card) {
        var lat = parseFloat(card.dataset.dealerLat);
        var lng = parseFloat(card.dataset.dealerLng);
        if (isNaN(lat) || isNaN(lng)) return;
        if (this.currentView === 'list') this.toggleView('both');

        var targetMarker = null;
        for (var i = 0; i < this.markers.length; i++) {
            var ml = this.markers[i].getLatLng();
            if (Math.abs(ml.lat - lat) < 0.0001 && Math.abs(ml.lng - lng) < 0.0001) {
                targetMarker = this.markers[i]; break;
            }
        }
        if (!targetMarker || !this.map) return;
        targetMarker.openPopup();

        var markerEl = targetMarker.getElement();
        if (markerEl) {
            markerEl.classList.add('dealer-map-marker--pulse');
            setTimeout(function () { markerEl.classList.remove('dealer-map-marker--pulse'); }, 2500);
        }

        this.dealerCards.forEach(function (c) { c.classList.remove('rymar-dealer-card--highlight'); });
        card.classList.add('rymar-dealer-card--highlight');
        var cardRef = card;
        setTimeout(function () { cardRef.classList.remove('rymar-dealer-card--highlight'); }, 2500);
    }

    initMap() {
        if (!this.mapContainer) {
            if (this.mapLoading) this.mapLoading.classList.add('hidden');
            return;
        }
        if (typeof L === 'undefined') {
            if (this.mapLoading) this.mapLoading.innerHTML = '<p style="color:#2B5E29;">Map library failed to load.</p>';
            return;
        }

        var validDealers = this.dealers.filter(function (d) {
            return d.location.lat !== 0 && d.location.lng !== 0;
        });

        if (validDealers.length === 0) {
            if (this.mapLoading) this.mapLoading.innerHTML = '<p>No locations with coordinates found.</p>';
            return;
        }

        if (this.mapLoading) this.mapLoading.classList.add('hidden');

        this.map = L.map(this.mapContainer, { scrollWheelZoom: false });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
            maxZoom: 19,
            subdomains: 'abcd'
        }).addTo(this.map);

        this.markerGroup = L.featureGroup();

        var officeIcon = L.divIcon({
            className: 'dealer-map-marker dealer-map-marker--office',
            html: '<div class="dealer-map-marker__dot dealer-map-marker__dot--office"></div>',
            iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -16]
        });

        var serviceIcon = L.divIcon({
            className: 'dealer-map-marker dealer-map-marker--service',
            html: '<div class="dealer-map-marker__dot dealer-map-marker__dot--service"></div>',
            iconSize: [16, 16], iconAnchor: [8, 8], popupAnchor: [0, -10]
        });

        var self = this;
        validDealers.forEach(function (dealer) {
            var icon = (dealer.type === 'office') ? officeIcon : serviceIcon;
            var marker = L.marker([dealer.location.lat, dealer.location.lng], { icon: icon });

            var popupHtml = '';

            if (dealer.type === 'office') {
                var addrParts = [];
                if (dealer.address.street) addrParts.push(dealer.address.street);
                if (dealer.address.city) addrParts.push(dealer.address.city);
                if (dealer.address.province) addrParts.push(dealer.address.province);
                if (dealer.address.postal) addrParts.push(dealer.address.postal);
                if (dealer.address.country) addrParts.push(dealer.address.country);
                var addrEncoded = encodeURIComponent(addrParts.join(', '));
    
                var bizParts = ['Rymar Grass'];
                if (dealer.address.city) bizParts.push(dealer.address.city);
                if (dealer.address.province) bizParts.push(dealer.address.province);
                var bizEncoded = encodeURIComponent(bizParts.join(' '));

                popupHtml += '<div style="min-width:180px;padding:4px;">' +
                    '<h4 style="margin:0 0 4px;font-size:16px;font-weight:700;">' + dealer.name + (dealer.address.province ? ', ' + dealer.address.province : '') + '</h4>' +
                    '<span style="display:inline-block;padding:4px 10px;background:rgba(31, 122, 62, 0.8);border:1px solid rgba(255,255,255,0.3);color:#ffffff;font-size:10.5px;text-transform:uppercase;border-radius:40px;margin-bottom:8px;font-weight:700;">DISTRIBUTION & SHOWROOM</span>' +
                    '<p style="margin:0 0 4px;font-size:13.5px;line-height:1.4;color:#555;">' +
                    (dealer.address.street ? dealer.address.street + '<br>' : '') +
                    dealer.address.city + (dealer.address.province ? ', ' + dealer.address.province : '') +
                    (dealer.address.postal ? ' ' + dealer.address.postal : '') +
                    '</p>' +
                    (dealer.phone ? '<p style="margin:4px 0;font-size:13.5px;color:#555;">' + dealer.phone + '</p>' : '') +
                    (dealer.servicing ? '<p style="margin:4px 0;font-size:12px;color:#666;"><strong>Servicing:</strong> ' + dealer.servicing + '</p>' : '') +
                    '<div style="display:flex;gap:12px;margin-top:10px;flex-wrap:wrap;">' +
                    '<a href="https://www.google.com/maps/dir/?api=1&destination=' + addrEncoded + '" ' +
                    'target="_blank" rel="noopener" style="color:#2B5E29;text-decoration:none;font-size:13px;font-weight:600;">Directions &rarr;</a>' +
                    '<a href="https://www.google.com/maps/search/?api=1&query=' + bizEncoded + '" ' +
                    'target="_blank" rel="noopener" style="color:#2B5E29;text-decoration:none;font-size:13px;font-weight:600;">View on Maps &rarr;</a>' +
                    (dealer.website ? '<a href="' + dealer.website + '" target="_blank" rel="noopener" ' +
                        'style="color:#2B5E29;text-decoration:none;font-size:12px;font-weight:600;">Website &rarr;</a>' : '') +
                    '</div></div>';
            } else {
                popupHtml += '<div style="min-width:180px;padding:4px;">' +
                    '<h4 style="margin:0 0 4px;font-size:16px;font-weight:700;">' + dealer.name + '</h4>' +
                    '<span style="display:inline-block;padding:2px 6px;background:#eef5ee;color:#2B5E29;font-size:11px;text-transform:uppercase;border-radius:3px;margin-bottom:8px;font-weight:600;">Service Area</span>' +
                    (dealer.servicing ? '<p style="margin:4px 0;font-size:12.5px;color:#666;"><strong>Servicing:</strong> ' + dealer.servicing + '</p>' : '') +
                    (dealer.phone ? '<p style="margin:4px 0;font-size:13.5px;color:#555;"><strong>Phone:</strong> ' + dealer.phone + '</p>' : '') +
                    (dealer.email ? '<p style="margin:4px 0;font-size:13.5px;color:#555;"><strong>Email:</strong> <a href="https://rymargrass.ca/pages/contact" style="color:#2B5E29;text-decoration:none;font-weight:600;">Contact Us</a></p>' : '') +
                    '</div>';
            }

            marker.bindPopup(popupHtml);
            marker.on('mouseover', function () { 
                this.openPopup(); 
                if (dealer.type !== 'office') {
                    self.showServiceArea(dealer.location.lat, dealer.location.lng);
                }
            });
            marker.on('mouseout', function () {
                var m = this;
                self.hideServiceArea();
                setTimeout(function () {
                    var popupEl = m.getPopup().getElement();
                    if (!popupEl || !popupEl.matches(':hover')) m.closePopup();
                }, 400);
            });
            marker.on('click', function () { self.highlightCard(dealer); });
            marker.dealerRef = dealer;
            self.markers.push(marker);
            self.markerGroup.addLayer(marker);
        });

        this.markerGroup.addTo(this.map);
        this.map.fitBounds([
            [42.0, -140.0],
            [70.0, -55.0]
        ]);
        this.map.once('click', function () { self.map.scrollWheelZoom.enable(); });
    }

    updateMapMarkers() {
        if (!this.map || !this.markerGroup) return;
        var self = this;
        var hasVisible = false;
        var bounds = L.latLngBounds();

        this.markers.forEach(function (marker) {
            var isVisible = self.filteredDealers.includes(marker.dealerRef);
            if (isVisible) {
                if (!self.markerGroup.hasLayer(marker)) self.markerGroup.addLayer(marker);
                bounds.extend(marker.getLatLng());
                hasVisible = true;
            } else {
                self.markerGroup.removeLayer(marker);
            }
        });

        if (hasVisible) {
            // If showing all dealers (no filters active), use a fixed wide view for stability
            if (this.filteredDealers.length === this.dealers.length) {
                this.map.fitBounds([
                    [42.0, -140.0],
                    [70.0, -55.0]
                ]);
            } else {
                this.map.fitBounds(bounds.pad(0.35));
            }
        }
    }
}

// ---- Initialize ----
(function () {
    function initRymarDealerLocators() {
        if (window.rymarDealerLocatorData) {
            var keys = Object.keys(window.rymarDealerLocatorData);
            for (var i = 0; i < keys.length; i++) {
                try {
                    new RymarDealerLocator(keys[i]);
                } catch (e) {
                    console.error('Rymar Dealer Locator init error:', e);
                }
            }
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initRymarDealerLocators);
    } else {
        initRymarDealerLocators();
    }
})();
